import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { JobAssignment, KeywordInput, SsoProviderConfigInput, SsoProviderId, SupportPriority, SupportStatus, TenantRole, TenantSettingsPatch, UserStatus, OrgImportRowsInput } from '@/lib/types';

export const orgKeys = {
  users: ['org', 'users'] as const,
  user: (id: string) => ['org', 'users', id] as const,
  invitations: ['org', 'invitations'] as const,
  usage: ['org', 'usage'] as const,
  settings: ['org', 'settings'] as const,
  sso: ['org', 'sso'] as const,
  keywords: ['org', 'keywords'] as const,
};

export function useUsers(enabled = true) {
  return useQuery({ queryKey: orgKeys.users, queryFn: () => api.users.list(), enabled });
}

export function useUserDetail(id: string | null) {
  return useQuery({ queryKey: orgKeys.user(id ?? ''), queryFn: () => api.users.get(id as string), enabled: !!id });
}

export function useTenantInvitations() {
  return useQuery({ queryKey: orgKeys.invitations, queryFn: () => api.invitations.list() });
}

export function useUsage() {
  return useQuery({ queryKey: orgKeys.usage, queryFn: () => api.reports.usage() });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; tenantRole: TenantRole; jobs?: JobAssignment[]; message?: string }) => api.invitations.create(body),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: orgKeys.invitations }), qc.invalidateQueries({ queryKey: orgKeys.users }), qc.invalidateQueries({ queryKey: ['jobs'] })]),
  });
}

/** Replace a person's job assignments (PM Admin / PM User per job). */
export function useSetUserJobs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, jobs }: { id: string; jobs: JobAssignment[] }) => api.users.setJobs(id, jobs),
    onSuccess: (_d, v) => Promise.all([qc.invalidateQueries({ queryKey: orgKeys.users }), qc.invalidateQueries({ queryKey: orgKeys.user(v.id) }), qc.invalidateQueries({ queryKey: ['jobs'] })]),
  });
}

export function useRevokeTenantInvitation() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.invitations.revoke(id), onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.invitations }) });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { tenantRole?: TenantRole; status?: UserStatus; firstName?: string; lastName?: string } }) => api.users.update(id, body),
    onSuccess: (_d, v) => Promise.all([qc.invalidateQueries({ queryKey: orgKeys.users }), qc.invalidateQueries({ queryKey: orgKeys.user(v.id) })]),
  });
}

// ---- support requests (Help page) ----
export interface SupportListParams {
  status?: SupportStatus;
  page?: number;
  pageSize?: number;
}

export const supportKeys = {
  all: ['org', 'support'] as const,
  list: (params: SupportListParams = {}) => ['org', 'support', 'list', params.status ?? 'all', params.page ?? 1, params.pageSize ?? 25] as const,
};

/** One page of the organization's help requests plus the header counts; the previous page stays up while the next loads. */
export function useSupportRequests(params: SupportListParams = {}) {
  return useQuery({ queryKey: supportKeys.list(params), queryFn: () => api.support.list({ status: params.status, page: params.page ?? 1, pageSize: params.pageSize ?? 25 }), placeholderData: keepPreviousData });
}

export function useUpdateSupportRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status?: SupportStatus; priority?: SupportPriority; internalNotes?: string | null } }) => api.support.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: supportKeys.all }),
  });
}

/** Effective organization settings + language catalog (any signed-in user; org admins can change them). */
export function useTenantSettings(enabled = true) {
  return useQuery({ queryKey: orgKeys.settings, queryFn: () => api.tenant.settings(), enabled, staleTime: 60_000 });
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: TenantSettingsPatch) => api.tenant.updateSettings(patch),
    onSuccess: (data) => {
      qc.setQueryData(orgKeys.settings, data);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useTenantSso(enabled = true) {
  return useQuery({ queryKey: orgKeys.sso, queryFn: () => api.tenant.sso(), enabled, staleTime: 30_000 });
}

export function useUpdateTenantSso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, body }: { provider: SsoProviderId; body: SsoProviderConfigInput }) => api.tenant.updateSso(provider, body),
    onSuccess: (data) => {
      qc.setQueryData(orgKeys.sso, data);
      void qc.invalidateQueries({ queryKey: orgKeys.settings });
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useOrgKeywords(enabled = true) {
  return useQuery({ queryKey: orgKeys.keywords, queryFn: () => api.orgKeywords.list(), enabled });
}

export function useOrgKeywordMutations() {
  const qc = useQueryClient();
  const invalidate = () => Promise.all([qc.invalidateQueries({ queryKey: orgKeys.keywords }), qc.invalidateQueries({ queryKey: ['jobs'] })]);
  const create = useMutation({ mutationFn: (body: KeywordInput) => api.orgKeywords.create(body), onSuccess: () => invalidate() });
  const update = useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<KeywordInput> }) => api.orgKeywords.update(id, body), onSuccess: () => invalidate() });
  const remove = useMutation({ mutationFn: (id: string) => api.orgKeywords.remove(id), onSuccess: () => invalidate() });
  return { create, update, remove };
}

// ---- sessions (Organization → Settings → Security) ----
export const sessionKeys = { summary: ['org', 'sessions'] as const };

export function useSessionsSummary(enabled = true) {
  return useQuery({ queryKey: sessionKeys.summary, queryFn: () => api.tenant.sessionsSummary(), enabled, staleTime: 15_000 });
}

export function useRevokeAllSessions() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.tenant.revokeAllSessions(), onSuccess: () => qc.invalidateQueries({ queryKey: sessionKeys.summary }) });
}

/** Spreadsheet import of users + job memberships (org admin). */
export function useImportUserRows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OrgImportRowsInput) => api.users.importRows(body),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: orgKeys.users }), qc.invalidateQueries({ queryKey: ['jobs'] })]),
  });
}

/** Web sign-ups and opt-outs made through the public page (Organization → Settings → Text sign-up). */
export function useWebConsentStats() {
  return useQuery({ queryKey: ['org', 'consent', 'stats'], queryFn: () => api.tenant.consentStats(), staleTime: 60_000 });
}
